import { useEffect, useRef, useState } from "react";

/**
 * StatCounter — displays a live number with:
 *  - Count-up animation from 0 → value on first mount
 *  - Locale-aware thousands separator
 *  - Optional "N,000+" rounding once `value` crosses a threshold
 *
 * Props:
 *   value        — target number (null while loading)
 *   locale       — "en-US" | "fr-FR" (default: "en-US")
 *   roundAt      — threshold above which we round down to nearest 1000 and add "+"
 *                  (default: 1000. Pass 0 to always show exact.)
 *   durationMs   — count-up animation duration (default: 900)
 *   suffix       — optional suffix rendered after the number (e.g. "documents")
 *   className    — passed through to the outer <span>
 *   placeholder  — shown while value is null (default: "—")
 */
const StatCounter = ({
  value,
  locale = "en-US",
  roundAt = 1000,
  durationMs = 900,
  suffix,
  className = "",
  style,
  placeholder = "—",
  testId,
}) => {
  const [displayed, setDisplayed] = useState(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);

  // Compute the "shown" target — if value >= roundAt, round down to nearest 1000
  const shownTarget = (() => {
    if (value == null) return null;
    if (roundAt > 0 && value >= roundAt) {
      return Math.floor(value / 1000) * 1000;
    }
    return value;
  })();
  const hasPlusSuffix = value != null && roundAt > 0 && value >= roundAt;

  useEffect(() => {
    if (shownTarget == null) return;

    // Cancel any in-flight animation and reset state before starting fresh.
    // (React StrictMode intentionally mounts effects twice in dev — resetting
    // here ensures the second pass runs the animation to completion.)
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startTimeRef.current = null;

    const from = 0;
    const to = shownTarget;
    setDisplayed(0);

    const step = (timestamp) => {
      if (startTimeRef.current === null) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(1, elapsed / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + (to - from) * eased);
      setDisplayed(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        // Ensure we land exactly on the target value at the end.
        setDisplayed(to);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [shownTarget, durationMs]);

  if (value == null) {
    return <span className={className} style={style} data-testid={testId}>{placeholder}</span>;
  }

  const nf = new Intl.NumberFormat(locale);
  const formatted = nf.format(displayed);

  return (
    <span className={className} style={style} data-testid={testId}>
      {formatted}
      {hasPlusSuffix ? "+" : ""}
      {suffix ? ` ${suffix}` : ""}
    </span>
  );
};

export default StatCounter;
